import { HttpClient } from '@angular/common/http';
import { inject, Injectable, LOCALE_ID, Optional } from '@angular/core';
import { InstrumentSearchApiService } from '@zero/search/api-instrument-search';
import { PushQuoteService } from '@zero/shared/ui-push';
import { AbstractDataService, AbstractMetaDataService } from '@zero/shared/util-abstract-services';
import { CustomerStateFacade, PositionsFacade } from '@zero/shared/util-global-state';
import {
  Asset,
  ChartSerie,
  ChartSerieType,
  InstrumentBaseDataModel,
  InstrumentChartModel,
  InstrumentHeaderModel,
  InstrumentNewsModel,
  InstrumentPositionModel,
  InstrumentStatisticsModel,
  InstrumentTopflopListItemModel,
  InstrumentTopflopListModel,
  InstrumentType,
  InstrumentUnderlyingModel,
  NewsItem,
  Quote,
  TelekursPeriod,
} from '@zero/shared/util-models';
import { StatisticsFacade } from '@zero/statistics/domain';
import { TradeFacade, TradingDayInfo } from '@zero/trade/domain';
import { INDICATION_QUOTE_MAPPINGS } from 'constants/shared-constants';
import { format, parse } from 'date-fns';
import { environment } from 'environments/environment';
import { isEmpty } from 'lodash-es';
import { BehaviorSubject, catchError, combineLatest, filter, firstValueFrom, map, of, Subscription, take, tap, withLatestFrom } from 'rxjs';
import { InstrumentDetailFacade } from '../../../application/instrument-detail.facade';
import { AssetTimelineInfo } from '../../../entities/asset-timeline-info.entity';
import { ChartRequest } from '../../../entities/chart-request.entity';
import { InstrumentDetail } from '../../../entities/instrument-detail.entity';
import { InstrumentBaseDataService } from '../instrument-base-data/instrument-base-data.service';
import { InstrumentNewsService } from '../instrument-news/instrument-news.service';

@Injectable({
  providedIn: 'root',
})
export class InstrumentDetailsService {
  readonly #positionsFacade = inject(PositionsFacade);
  readonly #assetSearchService = inject(InstrumentSearchApiService);
  readonly #pushQuoteService = inject(PushQuoteService);
  readonly #http = inject(HttpClient);
  readonly #newsService = inject(InstrumentNewsService);
  readonly #baseDataService = inject(InstrumentBaseDataService);
  readonly #rootDataService = inject(AbstractDataService);
  readonly #statisticsFacade = inject(StatisticsFacade);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #instrumentDetailFacade = inject(InstrumentDetailFacade);
  readonly #tradeFacade = inject(TradeFacade);
  readonly #localId = inject(LOCALE_ID);

  #isin: string;
  #asset: Asset;

  readonly #instrument = new BehaviorSubject<Asset | null>(null);
  readonly #quote = new BehaviorSubject<Quote | null>(null);
  readonly #marketData = new BehaviorSubject<InstrumentDetail | null>(null);
  readonly #instrumentType = new BehaviorSubject<InstrumentType | null>(null);
  readonly #chart = new BehaviorSubject<InstrumentChartModel>(new InstrumentChartModel());
  readonly #statistics = new BehaviorSubject<InstrumentStatisticsModel | null>(null);
  readonly #header = new BehaviorSubject<InstrumentHeaderModel | null>(null);
  readonly #news = new BehaviorSubject<InstrumentNewsModel | null>(null);
  readonly #position = new BehaviorSubject<InstrumentPositionModel | null>(null);
  readonly #baseData = new BehaviorSubject<InstrumentBaseDataModel | null>(null);
  readonly #documents = new BehaviorSubject(null);
  readonly #underlying = new BehaviorSubject<InstrumentUnderlyingModel | null>(null);
  readonly #indexTopflop = new BehaviorSubject<InstrumentTopflopListModel | null>(null);
  readonly #subscriptions: Subscription[] = [];
  readonly #tradingDayInfo = new BehaviorSubject<TradingDayInfo | null>(null);

  public readonly instrument$ = this.#instrument.asObservable();
  public readonly chart$ = this.#chart.asObservable();
  public readonly header$ = this.#header.asObservable();
  public readonly statistics$ = this.#statistics.asObservable();
  public readonly instrumentType$ = this.#instrumentType.asObservable();
  public readonly news$ = this.#news.asObservable();
  public readonly position$ = this.#position.asObservable();
  public readonly baseData$ = this.#baseData.asObservable();
  public readonly documents$ = this.#documents.asObservable();
  public readonly underlying$ = this.#underlying.asObservable();
  public readonly indexTopflop$ = this.#indexTopflop.asObservable();
  public readonly displayBaseData$ = combineLatest([this.baseData$, this.instrumentType$]).pipe(
    map(([baseData, instrumentType]) => baseData?.baseData && instrumentType && instrumentType !== InstrumentType.Derivative),
  );

  public isAssetAvailable = true;

  constructor(@Optional() private readonly _metaDataService: AbstractMetaDataService) {}

  public loadInstrument = async (isin: string): Promise<void> => {
    this.clear(this.#isin !== isin);
    this.#isin = isin;
    this.#asset = await this.#loadAsset();
    this.#instrument.next(this.#asset);
    if (!this.#asset?.name) {
      // product is not available in zero universe
      this.isAssetAvailable = false;
      return;
    }

    this.isAssetAvailable = true;

    this.#customerStateFacade.loadCustomerBaseData();
    this.#setMetaData();
    this.#setHeader();
    this.#setInstrumentType();
    this.#setDocuments();
    this.#determineAssetInPortfolio();
    await this.#loadMarketData();
    this.#loadNews();
    this.#loadBaseData();
    this.#subscribeToPushQuote();
    this.#subscribeToMarketDataAndTradingDayInfo();
    this.#subscribeToDividendYield();
    this.#tradeFacade.loadTradingDayInfo(isin);

    if (this.#asset.instrumentType === InstrumentType.Index) {
      this.#loadIndexTopflopData()
        .then(() => {
          this.#loadPositionsToTopflopData();
          this.#subscribeToTopflopQuotes();
        })
        .catch((e) => {
          console.error('loadIndexTopflopData failed', e);
        });
    }

    if (this.#asset.instrumentType === InstrumentType.Derivative) {
      this.#instrumentDetailFacade.loadDerivativeData(isin);
      this.#subscriptions.push(
        this.#instrumentDetailFacade.derivativeUnderlyingData$
          .pipe(filter((underlying) => !!underlying?.isin && !!underlying?.name))
          .subscribe(({ isin, name }) => this.#setUnderlying(isin, name)),
      );
    }
  };

  #subscribeToMarketDataAndTradingDayInfo(): void {
    this.#subscriptions.push(
      combineLatest([this.#marketData, this.#tradeFacade.tradingDayInfo$])
        .pipe(filter(([marketData, tradingDayInfo]) => !!marketData && !!tradingDayInfo))
        .subscribe(([marketData, tradingDayInfo]) => {
          this.#tradingDayInfo.next(tradingDayInfo);
          this.#setStatistics(marketData);
          this.#setChart(marketData, tradingDayInfo);
        }),
    );
  }

  readonly #getCurrentPrice = (quote: Quote): number => quote.bid ?? quote.ask ?? 0;

  readonly #setInstrumentType = (): void => this.#instrumentType.next(this.#asset.instrumentType);

  #setDocuments(): void {
    if (!this.#asset.salesbrochure && !this.#asset.semiAnnualReport && !this.#asset.annualReport && !this.#asset.baseInfoLink) {
      return;
    }
    const { salesbrochure, semiAnnualReport, annualReport, baseInfoLink } = this.#asset;
    this.#documents.next({
      salesbrochure,
      semiAnnualReport,
      annualReport,
      baseInfoLink,
    });
  }

  async #loadBaseData(): Promise<void> {
    try {
      const baseData = await this.#baseDataService.loadBaseData(this.#asset);
      this.#baseData.next(baseData);
      this.#header.next({
        ...this.#header.value,
        marketCap: baseData?.keyData?.marketCap,
        kgv: baseData?.keyData?.kgv,
      });
    } catch (e) {
      console.error('loading base data failed', e);
    }
  }

  async #loadNews(): Promise<void> {
    if (!this.#asset) {
      return;
    }

    const { instrumentType } = this.#asset;
    if (instrumentType !== InstrumentType.Derivative && instrumentType !== InstrumentType.Share) {
      return;
    }

    const { underlyingIsin, isin } = this.#asset;
    const isinToUse = underlyingIsin ?? isin;
    if (!isinToUse) {
      return;
    }

    const { name, underlyingName } = this.#asset;

    let news: NewsItem[];
    try {
      news = await firstValueFrom(this.#newsService.loadNews(isinToUse));
    } catch (e) {
      // no news available
      news = [];
    }

    this.#news.next({
      name: underlyingName ?? name,
      news,
    });
  }

  #setMetaData(): void {
    if (this._metaDataService?.setTitle) {
      const { name, instrumentType } = this.#asset;
      this._metaDataService?.setTitle(`${name} | ${instrumentType}`);
    }
  }

  #setHeader(): void {
    const {
      name,
      wkn,
      sparplan,
      isin,
      underlyingName,
      underlyingIsin,
      instrumentType,
      endfaellig,
      knockedOutFlag,
      priceScale,
      longName,
      shortName,
      iconUrl,
      quoteCurrencyCode,
      ranking,
      sentiment,
    } = this.#asset;

    const isDerivative = instrumentType === InstrumentType.Derivative;
    const isTypeTradable = this.#isTypeTradable();

    this.#header.next({
      ...this.#header.value,
      name,
      isin,
      wkn,
      isSavingsplanReady: sparplan,
      underlyingIsin: underlyingIsin ?? undefined,
      underlyingName: underlyingName ?? undefined,
      instrumentType,
      isEndfaellig: isDerivative && endfaellig,
      isKnockedOut: isDerivative && knockedOutFlag,
      priceScale,
      longName,
      shortName,
      iconUrl,
      isTypeTradable,
      currencyCode: quoteCurrencyCode,
      currencySymbol: Intl.NumberFormat(this.#localId, {
        style: 'currency',
        currency: quoteCurrencyCode,
      })
        .formatToParts()
        .find((part) => part.type === 'currency').value,
      sentiment,
      ranking,
    });
  }

  public clear = (isinChanged = true): void => {
    this.#subscriptions.length && this.#subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.#pushQuoteService.unsubscribe(this.#isin);
    this.#statisticsFacade.clearDividendYieldTotalByPosition();

    if (isinChanged) {
      this.#instrument.next(null);
      this.#quote.next(null);
      this.#marketData.next(null);
      this.#instrumentType.next(null);
      this.#header.next(null);
      this.#chart.next(new InstrumentChartModel());
      this.#statistics.next(null);
      this.#position.next(null);
      this.#news.next(null);
      this.#baseData.next(null);
      this.#documents.next(null);
      this.#underlying.next(null);
      this.#news.next(null);
      this.#indexTopflop.next(null);
      this.#tradingDayInfo.next(null);
    }
    this.#positionsFacade.unsubscribeFromPositionQuotes();
  };

  public changeChartPeriod = (period: string): void => {
    this.#chart.value.periods = this.#chart.value.periods.map((p) => {
      p.isActive = p.value === period;
      return p;
    });

    if (!this.#marketData.value) {
      this.#notifyChartError();
      return;
    }

    this.#loadChart(period, this.#marketData.value, this.#tradingDayInfo.value);
  };

  #notifyStatistics(current: number, quoteTime: Date): void {
    this.#statistics.next({
      ...this.#statistics.value,
      quoteTime,
    });
    if (current >= this.#statistics.value?.dayHigh) {
      this.#statistics.next({
        ...this.#statistics.value,
        dayHigh: current,
      });
    }

    if (current <= this.#statistics.value?.dayLow) {
      this.#statistics.next({
        ...this.#statistics.value,
        dayLow: current,
      });
    }

    if (this.#statistics.value?.yearHigh && current >= this.#statistics.value.yearHigh) {
      this.#statistics.next({
        ...this.#statistics.value,
        yearHigh: current,
        yearHighDate: new Date(),
      });
    }

    if (this.#statistics.value?.yearLow && current <= this.#statistics.value.yearLow) {
      this.#statistics.next({
        ...this.#statistics.value,
        yearLow: current,
        yearLowDate: new Date(),
      });
    }
  }

  readonly #getPerfAbs = (curr: number, prev: number) => (prev ? curr - prev : 0);
  readonly #getPerfPct = (perfAbs: number, prev: number) => (perfAbs ? (perfAbs / prev) * 100 : 0);

  #subscribeToPushQuote(): void {
    const quoteIsin = INDICATION_QUOTE_MAPPINGS.find((mapping) => mapping.isin === this.#isin)?.quoteIsin ?? this.#isin;
    const subscription = this.#pushQuoteService
      .subscribe(quoteIsin)
      .pipe(filter((quote) => !isEmpty(quote)))
      .subscribe((quote) => {
        if (!quote) {
          return;
        }

        this.#quote.next(quote);

        const current = this.#getCurrentPrice(quote);
        let prevDayClose = current;
        let prevDayBid = 0;
        let prevDayAsk = 0;
        if (this.#instrument.value) {
          const { historicQuotes } = this.#instrument.value;
          const historicQuote = historicQuotes?.length ? historicQuotes.slice().sort((a, b) => (a.ts > b.ts ? -1 : 1))[0] : null;

          prevDayClose = historicQuote?.bid ? historicQuote.bid : current;
          prevDayBid = historicQuote?.bid ?? 0;
          prevDayAsk = historicQuote?.ask ?? 0;
        }
        const perfAbs = this.#getPerfAbs(current, prevDayClose);
        const perfPct = this.#getPerfPct(perfAbs, prevDayClose);
        const perfBidAbs = this.#getPerfAbs(quote.bid, prevDayBid);
        const perfBidPct = this.#getPerfPct(perfBidAbs, prevDayBid);
        const perfAskAbs = this.#getPerfAbs(quote.ask, prevDayAsk);
        const perfAskPct = this.#getPerfPct(perfAskAbs, prevDayAsk);
        const quoteTime = new Date(quote.time);
        const isTradable = !this.#isDerivativeKnockedOut() && !this.#isDerivativeEndfaellig();

        this.#header.next({
          ...this.#header.value,
          ask: quote.ask,
          bid: quote.bid,
          quote: current,
          perfAbs,
          perfPct,
          perfAskAbs,
          perfAskPct,
          perfBidAbs,
          perfBidPct,
          quoteTime,
          isBuyable: isTradable && !this.#isSoldOut(quote),
          isSellable: isTradable,
          prevDayClose,
          prevDayBid,
          prevDayAsk,
        });

        this.#statistics.next({
          ...this.#statistics.value,
          ask: quote.ask,
          bid: quote.bid,
          currencyCode: this.#asset.quoteCurrencyCode,
        });

        this.#notifyStatistics(current, quoteTime);

        if (this.#position.value?.quantity) {
          const bid = quote.bid ?? 0;
          const { quantity, buyingValue } = this.#position.value;
          const currentValue = bid * quantity;
          this.#position.next({
            ...this.#position.value,
            currentValue,
            quote: bid,
            perfAbs: currentValue - buyingValue,
            perfPct: ((currentValue - buyingValue) / buyingValue) * 100,
          });
        }

        if (this.#chart.value.series?.length) {
          this.#chart.next({
            ...this.#chart.value,
            tick: {
              Last: current,
              DT: quoteTime.getTime(),
              Ask: quote.ask,
              Bid: quote.bid,
            },
            prevDayClose,
            prevDayBid,
            prevDayAsk,
            isTick: true,
          });
        }
      });

    this.#subscriptions.push(subscription);
  }

  #determineAssetInPortfolio() {
    if (this.#rootDataService.selectedCustomerChanged) {
      this.#subscriptions.push(
        this.#rootDataService.selectedCustomerChanged.pipe(filter((newCust) => !!newCust)).subscribe(async () => {
          this.#subscriptions.push(this.#subscribeToPosition());
          this.#positionsFacade.subscribeToPositionQuotes();
        }),
      );
    } else {
      this.#subscriptions.push(this.#subscribeToPosition());
      this.#positionsFacade.subscribeToPositionQuotes();
    }
  }

  #subscribeToPosition(): Subscription {
    this.#positionsFacade.loadPositions();
    return this.#positionsFacade
      .getPositionByIsin$(this.#isin)
      .pipe(
        tap((position) =>
          this.#header.next({ ...this.#header.value, hasAssetInPortfolio: !!position, availableQuantity: position?.availableQuantity }),
        ),
        filter((position) => !!position),
      )
      .subscribe(({ avgEntryQuote, quantity, quote, perfAbs, perfPct, availableQuantity }) =>
        this.#position.next({
          ...this.#position.value,
          buyingValue: avgEntryQuote * quantity,
          avgEntryQuote: avgEntryQuote,
          currentValue: quote * quantity,
          quote,
          quantity,
          perfAbs: perfAbs,
          perfPct: perfPct,
          available: availableQuantity,
        }),
      );
  }

  readonly #loadAsset = async (): Promise<Asset> => this.#loadAssetByIsin(this.#isin);

  readonly #loadAssetByIsin = async (isin: string) => firstValueFrom(this.#assetSearchService.getAssetByIsin(isin));

  async #loadMarketData(): Promise<void> {
    const url = `${environment.apihost + environment.instrumentServiceUrl}/detail/${this.#isin}`;

    try {
      this.#marketData.next(await firstValueFrom(this.#http.get<InstrumentDetail>(url)));
    } catch (e: unknown) {
      this.#marketData.next(null);
      this.#notifyChartError();
    }
  }

  #setStatistics(marketData: InstrumentDetail): void {
    if (!marketData) {
      return;
    }

    const { prevDay, high, low, w52High, w52HighDate, w52low, w52LowDate, performanceCurrentYear } = marketData;
    this.#statistics.next({
      ...this.#statistics.value,
      prevDayClose: prevDay,
      dayHigh: high,
      dayLow: low,
      yearHigh: w52High,
      yearHighDate: this.#parseGermanDate(w52HighDate),
      yearLow: w52low,
      yearLowDate: this.#parseGermanDate(w52LowDate),
      performanceCurrentYear,
      isin: this.#isin,
      hasDataToShow: true,
    });
  }

  #setChart(marketData: InstrumentDetail, tradingDayInfo: TradingDayInfo) {
    this.#loadChart(this.#chart.value.getActivePeriod(), marketData, tradingDayInfo);
  }

  async #getChartRequestParams(
    period: string,
    marketData: InstrumentDetail,
    tradingDayInfo: TradingDayInfo,
    isBestAccuracyPossible: boolean,
    firstTradingDateOfInstrument?: Date,
  ) {
    let instruments = marketData.tkIdentifier;

    if (period === TelekursPeriod.BestAccuracy) {
      const bestAccuracyPrefix = '49,Y0100532000';
      instruments = `${bestAccuracyPrefix}${marketData.isin},,`;

      if (!isBestAccuracyPossible) {
        period = TelekursPeriod.Intraday;
        instruments = marketData.tkIdentifierIntraday ?? marketData.tkIdentifier;
      }
    }

    const request = {
      period,
      instruments,
    } as ChartRequest;

    if (period === TelekursPeriod.BestAccuracy && tradingDayInfo) {
      let date = new Date();
      const { tradingToday, tradingStarted, previousTradingDay } = tradingDayInfo;
      if (!tradingToday || !tradingStarted) {
        date = new Date(previousTradingDay);
      }

      const dateFormat = 'yyyy-MM-dd HH:mm:ss';
      request.unit = 'minute';
      date.setHours(0, 0, 0, 0);
      request.from = format(date, dateFormat);
      date.setHours(22, 0, 0, 0);
      request.to = format(date, dateFormat);
    } else if (period === TelekursPeriod.CSI && firstTradingDateOfInstrument) {
      request.period = null;
      const dateFormat = 'yyyyMMdd';
      request.from = format(firstTradingDateOfInstrument, dateFormat);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      request.to = format(tomorrow, dateFormat);
    }

    return request;
  }

  //TODO: remove for global feature enable
  #isFeatureEnabled(): boolean {
    return localStorage.getItem('bestaccuracy-feature-enabled') === 'true';
  }

  async #loadChart(period: string, marketData: InstrumentDetail, tradingDayInfo: TradingDayInfo) {
    const { tkIdentifier } = marketData;
    if (!tkIdentifier) {
      this.#notifyChartError();
      return;
    }

    let assetTimelineInfo: AssetTimelineInfo | null = null;
    if (period === TelekursPeriod.CSI) {
      assetTimelineInfo = await firstValueFrom(this.#instrumentDetailFacade.assetTimelineInfo$);
      if (!assetTimelineInfo?.firstItem?.sortingDateTime || assetTimelineInfo?.firstItem?.orderDirection !== 'buy') {
        this.#notifyChartError('info', 'instrument-detail.instrument-chart.error-csi');
        return;
      }
    }

    try {
      let isBestAccuracyPossible = this.#isFeatureEnabled();
      if (isBestAccuracyPossible && [(InstrumentType.Crypto, InstrumentType.Index)].includes(this.#asset.instrumentType)) {
        isBestAccuracyPossible = false;
      }
      if (isBestAccuracyPossible && marketData.exchange !== 'BMN') {
        isBestAccuracyPossible = false;
      }

      const params = await this.#getChartRequestParams(
        period,
        marketData,
        tradingDayInfo,
        isBestAccuracyPossible,
        assetTimelineInfo?.firstItem?.sortingDateTime,
      );
      let chartSeries = await firstValueFrom(
        this.#http.get<ChartSerie[]>(`${environment.finDataUrl}/chart`, {
          params: {
            ...params,
          },
        }),
      );

      if (period === TelekursPeriod.CSI) {
        chartSeries = this.#modifyCSIChartSeries(chartSeries, assetTimelineInfo);
      }

      if (this.#header.value?.quote) {
        chartSeries[chartSeries.length - 1] = {
          ...chartSeries[chartSeries.length - 1],
          Last: this.#header.value.quote,
          Close: this.#header.value.quote,
        };
      }

      const currentSeries =
        period === TelekursPeriod.BestAccuracy && isBestAccuracyPossible ? this.#getCurrentSeries() : (['last'] as ChartSerieType[]);

      this.#chart.next({
        ...this.#chart.value,
        isin: this.#isin,
        isOTC: this.#asset.isOTC,
        isLoaded: true,
        isTick: false,
        prevDayClose: this.#header.value?.prevDayClose,
        prevDayBid: this.#header.value?.prevDayBid,
        prevDayAsk: this.#header.value?.prevDayAsk,
        hasError: false,
        currentSeries,
        series: chartSeries,
        instrumentType: this.#asset.instrumentType,
        isBestAccuracyPossible,
        displaySeriesToggles: period === TelekursPeriod.BestAccuracy && isBestAccuracyPossible,
      });
    } catch (e) {
      this.#notifyChartError();
    }
  }

  #modifyCSIChartSeries(chartSeries: ChartSerie[], assetTimelineInfo: AssetTimelineInfo): ChartSerie[] {
    // Return original series if no valid timeline data
    if (!assetTimelineInfo?.firstItem?.sortingDateTime) {
      return chartSeries;
    }

    const firstChartDate = new Date(assetTimelineInfo.firstItem.sortingDateTime);

    // Filter out any data points before the first trading date and time
    let modifiedSeries = chartSeries.filter((serie) => serie.Date && new Date(serie.Date) >= firstChartDate);

    // Add entry quote point only if it was a buy order and we have an average entry price
    const position = this.#position.value;
    const isBuyOrder = assetTimelineInfo.firstItem.orderDirection === 'buy';
    const hasEntryQuote = position?.avgEntryQuote != null;

    if (isBuyOrder && hasEntryQuote) {
      // Create entry point data with the average entry quote
      const entryQuoteChartSerie: ChartSerie = {
        Date: format(firstChartDate, 'yyyy-MM-dd HH:mm'),
        Last: position.avgEntryQuote,
        Close: position.avgEntryQuote,
      };

      // Add entry point as first element in the series
      modifiedSeries = [entryQuoteChartSerie, ...modifiedSeries];
    }

    return modifiedSeries;
  }

  #getCurrentSeries(): ChartSerieType[] {
    return this.#asset.instrumentType === InstrumentType.Derivative
      ? (['bid', 'ask'] as ChartSerieType[])
      : this.#chart.value.currentSeries;
  }

  readonly #parseGermanDate = (input: string | null): Date => (input ? parse(input, 'dd.MM.yyyy', new Date()) : undefined);

  readonly #notifyChartError = (severity: 'error' | 'info' = 'error', customMessage = ''): void =>
    this.#chart.next({
      ...this.#chart.value,
      series: [],
      isLoaded: true,
      isTick: false,
      hasError: true,
      severity,
      errorMessage: customMessage,
    });

  readonly #isDerivativeKnockedOut = (): boolean => this.#isDerivative() && this.#asset.knockedOutFlag;
  readonly #isDerivativeEndfaellig = (): boolean => this.#isDerivative() && this.#asset.endfaellig;
  readonly #isDerivative = (): boolean => this.#asset?.instrumentType === InstrumentType.Derivative;
  readonly #isTypeTradable = (): boolean =>
    !(
      this.#asset?.instrumentType === InstrumentType.Index ||
      this.#asset?.instrumentType === InstrumentType.Commodity ||
      this.#asset?.instrumentType === InstrumentType.Currency
    );
  readonly #isSoldOut = (quote: Quote | undefined): boolean =>
    quote ? this.#isDerivative() && !this.#asset.knockedOutFlag && quote.ask === 0 && quote.bid > 0 : false;

  async #loadIndexTopflopData(): Promise<void> {
    const url = `${environment.apihost + environment.instrumentServiceUrl}/indextopflop/${this.#isin}/50`;

    try {
      const loadIndexTopflopData = await firstValueFrom(this.#http.get<Asset[]>(url));
      return this.#initTopflop(loadIndexTopflopData);
    } catch (e: unknown) {
      this.#indexTopflop.next(null);
      throw e;
    }
  }

  #initTopflop(loadIndexTopflopData: Asset[]): Promise<void> {
    const sortedTopflopItems = loadIndexTopflopData.map((asset) => {
      const { name, isin, quote, sparplan, instrumentType, longName, shortName, iconUrl, historicQuotes } = asset;
      const current = quote?.bid ?? 0;
      const prevDayClose = historicQuotes?.[0]?.bid ?? current;
      const { perfAbs, perfPct } = this.#calculatePerformance(current, prevDayClose);
      const { isBuyable, isSellable } = this.#determineTradeStatus(quote);

      return {
        name: name ?? longName ?? shortName,
        isin,
        isSavingsplanReady: !!sparplan,
        instrumentType,
        priceScale: asset.priceScale ?? 2,
        iconUrl,
        quote: quote?.bid,
        prevDayClose,
        perfAbs,
        perfPct,
        isBuyable,
        isSellable,
      };
    });
    return this.#sortAndSplitTopflop(sortedTopflopItems);
  }

  async #loadPositionsToTopflopData(): Promise<void> {
    this.indexTopflop$
      .pipe(
        filter((model: InstrumentTopflopListModel) => !!model),
        filter(({ top, flop }) => !!top || !!flop),
        take(1),
        withLatestFrom(this.#positionsFacade.positionISINs$),
        filter((portfolioItems) => !!portfolioItems?.length),
        map(([indexTopflopItems, portfolioItems]) => ({
          top: indexTopflopItems.top?.map((item) => ({
            ...item,
            hasAssetInPortfolio: portfolioItems.includes(item.isin),
          })),
          flop: indexTopflopItems.flop?.map((item) => ({
            ...item,
            hasAssetInPortfolio: portfolioItems.includes(item.isin),
          })),
        })),
        filter((item) => !!item),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((topflopItems: InstrumentTopflopListModel) => {
        this.#indexTopflop.next({ top: topflopItems?.top, flop: topflopItems?.flop });
      });
  }

  async #subscribeToTopflopQuotes() {
    const indexTopflop = await firstValueFrom(this.#indexTopflop);
    if (indexTopflop === null) {
      return;
    }

    const allTopflop = this.#filterAndCombineTopflopData(indexTopflop);
    if (!allTopflop?.length) {
      return;
    }

    const isins = allTopflop.map((item) => item.isin);

    this.#subscriptions.push(
      this.#pushQuoteService.subscribeMultiBuffered(isins, false).subscribe((quotesMap) => {
        if (!quotesMap || !Object.keys(quotesMap).length) {
          return;
        }

        for (const isin in quotesMap) {
          const quote = quotesMap[isin];
          const currentTopflop = allTopflop.find((topFlopItem) => topFlopItem.isin === isin);
          const newTopflop = this.#mapTopflopQuote({ quote, currentTopflop });

          this.#updateTopflopQuotes((item) => item.isin === isin, newTopflop);
        }
      }),
    );
  }

  #filterAndCombineTopflopData(data: InstrumentTopflopListModel): InstrumentTopflopListItemModel[] {
    const top = data.top?.filter(Boolean) || [];
    const flop = data.flop?.filter(Boolean) || [];
    return [...top, ...flop];
  }

  #updateTopflopQuotes(
    predicate: (item: InstrumentTopflopListItemModel) => boolean,
    replacementObject: InstrumentTopflopListItemModel,
  ): Promise<void> {
    const allTopflopItems = this.#filterAndCombineTopflopData(this.#indexTopflop.getValue());
    const updatedTopflopItems = allTopflopItems.map((item) => (predicate(item) ? replacementObject : item));
    return this.#sortAndSplitTopflop(updatedTopflopItems);
  }

  #mapTopflopQuote({ quote, currentTopflop }: { quote: Quote; currentTopflop: InstrumentTopflopListItemModel }) {
    const current = quote?.bid ?? 0;
    const prevDayClose = currentTopflop.prevDayClose ?? current;
    const { perfAbs, perfPct } = this.#calculatePerformance(current, prevDayClose);
    const { isBuyable, isSellable } = this.#determineTradeStatus(quote);

    return {
      ...currentTopflop,
      quote: quote.bid,
      quoteTime: new Date(quote.time),
      perfAbs,
      perfPct,
      isBuyable,
      isSellable,
    };
  }

  #sortAndSplitTopflop(topflopItems: InstrumentTopflopListItemModel[]): Promise<void> {
    const sortedTopflopItems = topflopItems.sort((a, b) => (a.perfPct == null || a.perfPct < b.perfPct ? 1 : -1));
    const middleIndex = Math.floor(sortedTopflopItems.length / 2);

    this.#indexTopflop.next({
      top: sortedTopflopItems.slice(0, middleIndex),
      flop: sortedTopflopItems.slice(middleIndex),
    });

    return Promise.resolve();
  }

  #calculatePerformance(current: number, prevDayClose: number): { perfAbs: number; perfPct: number } {
    const perfAbs = current - prevDayClose;
    const perfPct = prevDayClose ? (perfAbs / prevDayClose) * 100 : 0;
    return { perfAbs, perfPct };
  }

  #determineTradeStatus(quote: Quote): { isBuyable: boolean; isSellable: boolean } {
    return {
      isBuyable: !!quote?.ask,
      isSellable: !!quote?.bid,
    };
  }

  #subscribeToDividendYield(): void {
    this.#statisticsFacade.loadDividendYieldTotalByPosition(this.#isin);
    this.#subscriptions.push(
      this.#statisticsFacade.dividendYieldTotalByPosition$
        .pipe(filter((dividend) => Boolean(dividend?.dividendAbsolute || dividend?.dividendYield)))
        .subscribe(({ dividendAbsolute, dividendYield }) =>
          this.#position.next({
            ...this.#position.value,
            dividendAbsolute,
            dividendYield,
          }),
        ),
    );
  }

  async #setUnderlying(isin: string, name: string): Promise<void> {
    if (!isin || !name) {
      return;
    }

    this.#pushQuoteService.unsubscribe(isin);
    const underlying = await this.#loadAssetByIsin(isin);
    if (!underlying || Object.keys(underlying).length === 0) {
      this.#underlying.next({
        name,
        isin,
        isTradable: false,
        currencyCode: null,
        quoteFormat: '1.2-2',
      });
      return;
    }

    const { historicQuotes } = underlying;
    this.#underlying.next({
      name,
      isin,
      isTradable: true,
      currencyCode: underlying.quoteCurrencyCode,
      quoteFormat: underlying.priceScale ? `1.${underlying.priceScale}-${underlying.priceScale}` : '1.2-2',
    });
    const quoteIsin = INDICATION_QUOTE_MAPPINGS.find((mapping) => mapping.isin === isin)?.quoteIsin ?? isin;

    this.#subscriptions.push(
      this.#pushQuoteService.subscribe(quoteIsin).subscribe((quote) => {
        const current = quote ? this.#getCurrentPrice(quote) : 0;

        let prevDayClose = current;
        if (historicQuotes) {
          const historicQuote = historicQuotes?.length ? historicQuotes.slice().sort((a, b) => (a.ts > b.ts ? -1 : 1))[0] : null;

          prevDayClose = historicQuote?.bid ? historicQuote.bid : current;
        }
        const perfAbs = this.#getPerfAbs(current, prevDayClose);
        const perfPct = this.#getPerfPct(perfAbs, prevDayClose);
        const quoteTime = quote?.time ? new Date(quote.time) : null;

        this.#underlying.next({
          ...this.#underlying.value,
          quote: quote ? this.#getCurrentPrice(quote) : 0,
          perfAbs,
          perfPct,
          quoteTime,
        });
      }),
    );
  }
}
